import { Suspense } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart as BarChartIcon } from "lucide-react";
import { EcosuiteErrorBoundary } from "@/components/error-boundary";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { CHART_COLORS } from "../constants";

type UserStat = {
  name: string;
  value: number;
  icon: JSX.Element;
};

interface UserStatsChartProps {
  statsData: UserStat[];
}

// Custom tooltip for bar chart
const CustomBarTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border-border rounded-md border p-2 text-xs shadow-sm">
        <p className="font-medium">{payload[0].payload.name}</p>
        <p className="text-muted-foreground mt-1">Count: {payload[0].value}</p>
      </div>
    );
  }
  return null;
};

function UserStatsChartContent({ statsData }: UserStatsChartProps) {
  return (
    <Card className="border-muted/50 overflow-hidden shadow-sm">
      <CardHeader className="bg-muted/10 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <BarChartIcon className="text-primary h-4 w-4" />
            <CardTitle className="text-base">User Statistics</CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="h-72 pt-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={statsData}
            margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--muted)"
              strokeOpacity={0.4}
            />
            <XAxis
              dataKey="name"
              tick={(props) => {
                const { x, y, payload } = props;
                return (
                  <g transform={`translate(${x},${y})`}>
                    <text
                      x={0}
                      y={0}
                      dy={16}
                      textAnchor="middle"
                      fill="currentColor"
                      className="text-foreground"
                      fontSize={11}
                    >
                      {payload.value}
                    </text>
                  </g>
                );
              }}
              stroke="var(--muted-foreground)"
            />
            <YAxis
              stroke="var(--muted-foreground)"
              tick={(props) => {
                const { x, y, payload } = props;
                return (
                  <g transform={`translate(${x},${y})`}>
                    <text
                      x={0}
                      y={0}
                      dx={-10}
                      textAnchor="end"
                      fill="currentColor"
                      className="text-foreground"
                      fontSize={11}
                    >
                      {payload.value}
                    </text>
                  </g>
                );
              }}
            />
            <RechartsTooltip
              content={<CustomBarTooltip />}
              cursor={{ fill: "transparent" }}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {statsData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export function UserStatsChart(props: UserStatsChartProps) {
  return (
    <EcosuiteErrorBoundary>
      <Suspense
        fallback={<LoadingScreen text="Loading chart..." className="h-72" />}
      >
        <UserStatsChartContent {...props} />
      </Suspense>
    </EcosuiteErrorBoundary>
  );
}
